import type {
  FloorsheetDay,
  FloorsheetTradesQuery,
} from '@/features/floorsheet/api/floorsheetService'
import { floorsheetOrder, type FloorsheetQuery } from '@/schemas/floorsheet'

/**
 * The trade log's argument AND its key payload, from one function, so anything the service varies
 * on is in the key and nothing else is.
 *
 * `page`/`pageSize` are here because the endpoint cuts the page. The sort head's pair becomes the
 * endpoint's `order` here, once, so two URLs that ask for the same order share one cache entry.
 * The fields are picked one by one rather than spread, so a whole `FloorsheetQuery` handed in
 * cannot put an unrelated param into the key.
 */
export const floorsheetTradesQuery = (
  query: FloorsheetQuery,
  day: FloorsheetDay,
): FloorsheetTradesQuery => ({
  page: query.page,
  pageSize: query.pageSize,
  symbol: query.symbol,
  broker: query.broker,
  buyer: query.buyer,
  seller: query.seller,
  day,
  order: floorsheetOrder(query.sortBy, query.sortDesc),
})

/**
 * Query key factory. `summary` and `insights` sit outside the trade log's key: they describe the
 * day, not the filters, so a symbol or a page turn must not reload the panel above the table or
 * the two cards below it.
 */
export const floorsheetKeys = {
  all: ['floorsheet'] as const,
  summary: (day: FloorsheetDay) => [...floorsheetKeys.all, 'summary', day] as const,
  trades: (query: FloorsheetTradesQuery) => [...floorsheetKeys.all, 'trades', query] as const,
  brokerOptions: () => [...floorsheetKeys.all, 'broker-options'] as const,
  insights: (day: FloorsheetDay) => [...floorsheetKeys.all, 'insights', day] as const,
}
